// ─────────────────────────────────────────────
// HUMD 2025 Investor Update - Content & Data
// Edit this file to update all copy and numbers
// ─────────────────────────────────────────────

export const META = {
  title: 'HUMD - 2025 Investor Update',
  date: 'March 2026',
  confidential: true,
};

export const COVER = {
  eyebrow: 'Annual Investor Update · 2025',
  title: '2025 was the year',
  titleEm: 'we built the foundation.',
  sub: 'March 2026 · Prepared for existing investors · Confidential',
  letter: [
    'Dear investor,',
    'The marketplace grew 69% year-on-year and covered all operational costs throughout 2025. We raised the $100K angel round we committed to. We grew the team. We built Humd Studio from the ground up using three years of real operational learning.',
    'Everything we built in 2025 comes to market in 2026. Saudi Arabia launches Q3. Humd Studio launches Q3. The team is in place. This update covers what we delivered, where the business stands, and what the year ahead looks like.',
  ],
};

export const TOC = [
  { num: '1', label: '2025 Snapshot', href: 'snapshot' },
  { num: '2', label: 'Commitments', href: 'commitments' },
  { num: '3', label: 'What We Built', href: 'built' },
  { num: '4', label: 'Financials', href: 'financials' },
  { num: '5', label: 'Customer Quality', href: 'retention' },
  { num: '6', label: 'Humd Studio', href: 'studio' },
  { num: '7', label: 'Saudi Arabia', href: 'saudi' },
  { num: '8', label: 'Q1 2026', href: 'q1' },
  { num: '9', label: '2026 Plan', href: 'plan' },
  { num: '10', label: 'How You Can Help', href: 'help' },
];

export const SNAPSHOT = {
  sectionNum: '01 · 2025 at a Glance',
  title: 'A deliberate',
  titleEm: 'building year',
  body: 'While the marketplace grew 69% and self-funded all operations, our primary focus was product infrastructure. We made a conscious choice to build Humd Studio correctly - drawing on three years of operational data - rather than ship something half-built to our vendor network.',
  stats: [
    { value: 1180000, format: 'compact', prefix: 'AED ', label: '2025 Revenue', sub: '+69% vs AED 760K in 2024' },
    { value: 90, suffix: '%', format: 'number', label: 'Annual Retention', sub: '35 of 39 customers returned' },
    { value: 3, suffix: 'M+', format: 'number', label: 'Products Delivered', sub: 'Since launch · 5+ countries' },
    { value: 18, format: 'number', label: 'Team Members', sub: '6 UAE · 12 overseas' },
    { value: 24.9, suffix: 'x', format: 'decimal', label: 'LTV:CAC Ratio', sub: 'Up from 8.3x in 2024' },
    { value: 150, suffix: '+', format: 'number', label: 'Vendor Partners', sub: 'UAE & Saudi Arabia' },
    { value: 20, format: 'number', label: 'Enterprise Clients', sub: 'Avg AED 32,000 per year' },
    { value: 100, suffix: 'K', prefix: '$', format: 'number', label: 'Angel Round Closed', sub: 'March 2025' },
  ],
};

export const COMMITMENTS = {
  sectionNum: '02 · Commitments',
  title: 'What we promised.',
  titleEm: 'What we delivered.',
  body: 'Two specific commitments were made at the close of our last funding round. Both were delivered.',
  items: [
    {
      badge: '✓ Delivered',
      title: '$100K Angel Round - Closed March 2025',
      body: 'We committed to closing a $100K angel investment to accelerate product development. Completed March 2025. Capital was deployed into Humd Studio build, the humd.ae v2 platform rebuild, and internal operational tooling - all now substantially complete.',
      callout: '$100K raised March 2025 · Deployed into product build, platform rebuild, and operational tooling.',
    },
    {
      badge: '✓ Delivered',
      title: 'Team Growth - 12 to 18 Members',
      body: 'We committed to growing the team to support product and operations. We closed 2025 with 18 members - 6 in UAE, 12 overseas - across product, technology, design, and operations. This team simultaneously delivered humd.ae v2, the enterprise OMS, the vendor tool, and the majority of Humd Studio.',
      callout: 'Team grew 12 to 18 members · 6 UAE · 12 overseas · Product, tech, design, operations.',
    },
  ],
};

export const BUILT = {
  sectionNum: '03 · What We Built',
  title: 'Five deliverables.',
  titleEm: 'All live or near launch.',
  body: 'Every product built in 2025 is either generating revenue today or directly enables a revenue stream in 2026. This was not speculative development - it was execution against a clear roadmap.',
  items: [
    {
      icon: '🏗️',
      title: 'humd.ae Version 2 - Live',
      body: 'Complete platform rebuild from the ground up. Enterprise-grade architecture, vendor portal, and enterprise dashboard fully integrated. Faster, more reliable, and capable of scaling to 10x current volume.',
    },
    {
      icon: '📊',
      title: 'Enterprise Order Management System - Live',
      body: 'Built specifically for large, complex orders. Supports end-to-end order flow from quote to delivery - reducing manual touchpoints, improving fulfilment accuracy, and giving enterprise clients a more professional experience.',
    },
    {
      icon: '🛠️',
      title: 'Vendor Tool - Live',
      body: 'The first product built for our vendors rather than our customers. Allows vendors to receive, manage, and fulfil marketplace orders without manual coordination from our team.',
    },
    {
      icon: '⚙️',
      title: 'Internal Automation Tools - Built & In Progress',
      body: 'Several tools have been built in-house to replace manual workflows across order processing, production tracking, and customer communication. A number of these are also being packaged into Humd Studio - the build cost is shared across internal gains and the product roadmap.',
    },
    {
      icon: '🚀',
      title: 'Humd Studio - Core Build Complete · Launching Q3 2026',
      body: 'The SaaS platform for the print, packaging and corporate gifting industry. Storefront, order management, design mockup tools, payments, and logistics - all in one platform. Built from 3 years of real operational data. 20+ vendors in active pipeline.',
    },
  ],
};

export const FINANCIALS = {
  sectionNum: '04 · Financial Performance',
  title: 'Revenue. Margin.',
  titleEm: 'Trajectory.',
  body: '2024 actuals vs 2025 actuals vs 2026 target. All operational expenses covered by marketplace revenue throughout. Zero debt. Zero liabilities.',
  scorecard: [
    { metric: 'Total Revenue (AED)', y2024: '760,000', y2025: '1,180,000', y2026: '2,250,000+', highlight: false },
    { metric: 'Gross Profit (AED)', y2024: '197,000', y2025: '443,000', y2026: '900,000+', highlight: true },
    { metric: 'Gross Margin %', y2024: '26%', y2025: '38%', y2026: '40%+', highlight: false },
    { metric: 'Net Loss (AED)', y2024: '(383,000)', y2025: '(293,000)', y2026: 'Breakeven Q4', highlight: true },
    { metric: 'Avg Order Value (AED)', y2024: '1,600', y2025: '3,200', y2026: '3,500+', highlight: false },
    { metric: 'Annual Retention Rate', y2024: '~78%', y2025: '90%', y2026: '90%+', highlight: true },
    { metric: 'LTV:CAC Ratio', y2024: '8.3x', y2025: '24.9x', y2026: '30x+', highlight: false },
    { metric: 'Enterprise Clients', y2024: '~10', y2025: '20', y2026: '35+', highlight: true },
    { metric: 'Profitable Months', y2024: '1', y2025: '2', y2026: 'Consistent Q3+', highlight: false },
  ],
  callout: {
    title: 'Three metrics that define 2025',
    points: [
      'Gross margin 26% → 38% - the business is becoming more efficient as it scales.',
      'LTV:CAC 8.3x → 24.9x - unit economics more than tripled. Every customer acquired is worth far more.',
      'Annual retention 90% - 35 of 39 customers returned. Retained customers grew spend by 79% year-on-year.',
    ],
  },
  note: 'Cash position: all operational expenses throughout 2025 were covered by marketplace revenue. Zero debt. Zero liabilities.',
  chartData: {
    revenue: {
      labels: ['2023 est', '2024', '2025', '2026 Target'],
      data: [608000, 760000, 1180000, 2250000],
    },
    margin: {
      labels: ['2023', '2024', '2025', '2026 Target'],
      data: [18, 26, 38, 42],
    },
  },
};

export const RETENTION = {
  sectionNum: '05 · Customer Quality',
  title: 'Retention that',
  titleEm: 'speaks for itself',
  body: 'Annual B2B retention - the right metric for a transactional model. Did a customer who ordered in 2024 return and order again in 2025?',
  bigNum: { value: 90, suffix: '%', label: 'Annual Retention Rate', sub: '35 of 39 tracked 2024 customers returned' },
  points: [
    { val: '4 lost', label: 'Customers who did not return', sub: 'All low value - combined under AED 10,000. Zero meaningful churn.' },
    { val: '+79%', label: 'Retained customer spend growth', sub: 'Same accounts spent AED 504K in 2025 vs AED 281K in 2024.' },
    { val: 'Land & expand', label: 'Accounts that started small, scaled significantly', sub: 'Multiple enterprise accounts grew 5–10x their spend year-on-year.' },
    { val: '76%', label: 'Q1 2026 repeat order rate', sub: '42 of 55 Q1 2026 orders from existing clients.' },
  ],
};

export const STUDIO = {
  sectionNum: '06 · Humd Studio',
  title: 'Launching Q3 2026.',
  titleEm: 'Pipeline is warm.',
  body: 'Humd Studio is the SaaS platform for print shops, packaging suppliers, gifting companies, and creative agencies. Built from 3 years of operating the marketplace manually - every feature fixes a real problem we documented.',
  pipelineCallout: {
    title: 'Why our vendor pipeline is warm, not cold',
    body: 'We have 150+ vendor partners across UAE and Saudi Arabia - relationships built over three years of working together on the marketplace. These vendors already trust us, have fulfilled orders through our platform, and know our team personally. 20+ are in active Humd Studio conversations right now.',
  },
  pricingCallout: {
    title: 'Pricing validated with vendors',
    body: 'AED 999/month (Pro) and AED 4,999/month (Enterprise) confirmed in market conversations. 1 month free on launch. 20+ vendors in active pipeline.',
  },
  revenueCallout: {
    title: 'Revenue potential at 150 vendor conversions',
    body: 'Blended mix across tiers = AED 150–180K/month recurring. At scale, Humd Studio alone reaches AED 1.8–2.1M ARR - independent of marketplace revenue.',
  },
};

export const SAUDI = {
  sectionNum: '07 · Saudi Arabia - humd.me',
  title: 'Validated.',
  titleEm: 'Launching Q3 2026.',
  body: '3-month pilot in Q3 2025. Zero dedicated infrastructure. Zero marketing spend. The model works beyond UAE - and the market is structurally larger.',
  pilot: [
    { month: 'January', rev: 'AED 10K', note: 'Cold start' },
    { month: 'February', rev: 'AED 20K', note: '+100% MoM' },
    { month: 'March', rev: 'AED 60K', note: '+200% MoM' },
    { month: 'Total', rev: 'AED 90K', note: '15 customers · 20+ vendors' },
  ],
  callout: {
    title: 'What this means for 2026',
    body: 'Revenue grew 6x in three months with zero marketing spend. 20+ Saudi vendors in active onboarding. humd.me launches Q3 2026 with supply, demand, and a proven playbook already in place. Saudi Arabia is structurally larger than UAE for our category.',
  },
};

export const Q1_2026 = {
  sectionNum: '08 · Q1 2026',
  title: 'Strong underlying',
  titleEm: 'momentum.',
  body: 'Q1 2026 delivered AED 502,000 - 74% ahead of Q1 2025. Regional instability across the Middle East impacted discretionary B2B spending in the market broadly, which accelerated our conviction to diversify into Saudi and launch Humd Studio sooner.',
  stats: [
    { value: 502000, format: 'compact', prefix: 'AED ', label: 'Q1 2026 Revenue', sub: '+74% vs Q1 2025' },
    { value: 76, suffix: '%', format: 'number', label: 'Repeat Order Rate', sub: '42 of 55 orders from existing clients' },
    { value: 50.3, suffix: 'x', format: 'decimal', label: 'LTV:CAC Ratio', sub: 'Strongest quarter recorded' },
    { value: 4500, prefix: 'AED ', format: 'number-comma', label: 'Marketing Spend', sub: '116x revenue return on spend' },
  ],
  chartData: {
    monthly: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      data: [90066, 94866, 103158, 72262, 105769, 51234, 113352, 61602, 118267, 151729, 147499, 69993],
    },
    q1Compare: {
      labels: ['January', 'February', 'March'],
      q1_2025: [90066, 94866, 103158],
      q1_2026: [104000, 95000, 325000],
    },
  },
};

export const PLAN = {
  sectionNum: '09 · 2026 Plan',
  title: 'Everything comes',
  titleEm: 'to market.',
  body: 'Three revenue streams. AED 2.25M+ target. Breakeven by Q4 2026. Q1 is already at AED 502K - 25% of a conservative estimate in 3 months.',
  revenue: [
    { stream: 'UAE Marketplace - humd.ae', y2025: 'AED 1,180,000', y2026: 'AED 1,400,000' },
    { stream: 'KSA Marketplace - humd.me', y2025: '-', y2026: 'AED 600,000' },
    { stream: 'Humd Studio - SaaS', y2025: '-', y2026: 'AED 250,000' },
  ],
  total: { stream: 'Total', y2025: 'AED 1,180,000', y2026: 'AED 2,250,000+' },
  milestones: [
    { when: 'Q3 2026', title: 'Humd Studio launches', body: 'Onboard first paying vendors. Begin converting 150+ vendor partner network to SaaS subscriptions.' },
    { when: 'Q3 2026', title: 'humd.me Saudi Arabia launches', body: 'Full marketplace live with 20+ vendors ready. Building on the AED 90K pilot momentum.' },
    { when: 'Q4 2026', title: 'Operational breakeven', body: 'Humd Studio + Saudi + UAE enterprise revenue crosses zero. On track for a profitable close to the year.' },
    { when: 'Q4 2026', title: 'Enterprise expansion', body: 'Grow from 20 to 35+ enterprise clients. Deepen existing accounts. Target 80%+ revenue from repeat clients.' },
    { when: '2026', title: '$1M Seed Round', body: 'Currently raising to accelerate Humd Studio, Saudi launch, and team growth. 35% product · 25% Saudi · 25% team · 15% marketing.' },
  ],
};

export const HELP = {
  sectionNum: '10 · How You Can Help',
  title: 'Beyond capital,',
  titleEm: 'three specific asks.',
  body: 'Your network is as valuable as your capital. Here are three areas where an introduction would make a meaningful difference in 2026.',
  items: [
    { icon: '🤝', title: 'Investor introductions', body: 'We are raising a $1M seed round. Warm introductions to GCC-focused investors, family offices, or angels interested in B2B SaaS or marketplace businesses.' },
    { icon: '🏢', title: 'Enterprise client connections', body: 'Introductions to heads of procurement, marketing, or operations at mid-to-large UAE and Saudi companies. Enterprise clients average AED 30,000–40,000 annually.' },
    { icon: '🇸🇦', title: 'Saudi Arabia network', body: 'As humd.me launches in Q3, introductions to established Saudi businesses or anyone with a strong Saudi business network would accelerate our market entry meaningfully.' },
  ],
};

export const CLOSING = {
  points: [
    'Marketplace growing and self-funding. 90% annual retention. 24.9x LTV:CAC.',
    'Humd Studio near complete - 20+ vendor partners in active pipeline, pricing validated.',
    'Saudi Arabia validated - AED 90K pilot, full launch Q3 2026 with 20+ vendors ready.',
    'Q1 2026: AED 502K (+74% YoY). AED 2.25M+ full year target on track.',
    'Team of 18 in place. Zero debt. Zero liabilities. Building with discipline.',
  ],
  sig: 'Vardha, Zak',
  sigSub: '& the Humd team',
  email: 'teamhumd@humd.co',
  domains: [
    { label: 'humd.ae', url: 'https://humd.ae' },
    { label: 'humd.me', url: 'https://humd.me' },
    { label: 'humd.co', url: 'https://humd.co' },
  ],
};
